// 🔤🎞 KineticTypo — 강의·인포그래픽 트랙의 «모션 타이포» 장표(회장님 2026-09-06 스레드 벤치마크 반영).
//   벤치마크(Astra 모션 그래픽)는 확산 모델이 아니라 코드 렌더(Remotion/HyperFrames)였다 — 우리 장표
//   스택과 동형이라 같은 문법을 그대로 얹는다: 글자별 바운스 등장 · 강조어 확대+물결 · 그라디언트 배경
//   + 파티클 · 2.5D 카메라 푸시인. ★글자는 실제 텍스트라 «한글 정확히 그대로»가 구조로 보장된다★
//   (영상 벤더의 no-text 정책과 무관·OCR 게이트 불필요 — DOM textContent === title 이 완료 조건).
//
// 계약: props = fullText 와 동일(title·sub + LayoutBaseProps). 설계도 브릿지가 첫·마지막 M씬의 짧은
//   fullText 를 이 타입으로 승격한다(msb-blueprint.promoteKineticTypo). 직접편집 폼도 fullText 와 공유.
// 렌더 함정(tokens.tsx 실측 계승): CSS radial-gradient·backgroundImage 타일은 web-renderer 캡처에서
//   안 칠해진다 → 배경은 SVG gradient. 파티클은 기존 렌더 증명 컴포넌트(FloatingOrbs·ParticleRise) 재사용.
import React from 'react';
import { AbsoluteFill, interpolate, spring, useVideoConfig } from 'remotion';
import { Confetti, FloatingOrbs, FW, KO_TEXT, LINE_HEIGHT, ParticleRise, SPRING, T, type AtmosphereColors } from './tokens';
import { slideIn, KitWords } from './motion';
import { enterAtFor, useSafeArea, type LayoutBaseProps } from './layouts';
import { SlideStageInsetContext } from './stage-inset';
import { briefGlueLastWords } from './motion-brief-shape';

/** 글자 등장 간격(프레임) — 어절 사이는 조금 더 쉰다(벤치마크의 «단어 단위 리듬»). ★기본 리듬★이고,
 *  구간이 짧으면 `beatPace` 가 이 셋을 같은 배율로 압축한다(아래). */
const CHAR_STAGGER = 2;
const WORD_GAP = 5;
const CHAR_ENTER_FRAMES = 18;

/**
 * 🕐 등장 리듬 자동 조절(⑤ 튜닝 · 회장님 2026-09-08 «자동 조절로 가자»).
 *
 * ★왜 필요했나(09-08 표본 실측)★: 등장 리듬이 고정이라 **문장이 길면 다 뜨기 전에 다음 구간으로
 *  넘어갔다** — 14자 1.8초 · 21자 2.4초 · 32자 3.3초 · 44자 4.3초인데, 구간 길이는 나레이션이
 *  정하므로(보통 3초 안팎) 30자를 넘으면 마지막 낱말이 화면에 뜨지도 못한 채 사라졌다.
 *
 * ★고르는 값은 «배율» 하나다★ — stagger·어절 간격·글자 등장 시간을 **같은 비율로** 줄인다.
 *  일부만 줄이면 리듬이 깨진다(글자는 빨리 시작하는데 하나하나는 느린 식).
 */
/** 구간의 몇 할 안에 문장이 다 떠야 하는가 — 나머지는 «읽을 틈»이다(다 뜨자마자 넘어가면 못 읽는다). */
const BEAT_READ_HOLD = 0.8;
/** 압축 하한 — 이보다 빠르면 글자별 등장이 «한 번에 나타남»과 구분되지 않아 연출이 사라진다. */
const MIN_PACE = 0.35;

/**
 * 이 문장이 `budgetFrames` 안에 다 뜨려면 기본 리듬의 몇 배로 가야 하는가.
 * 여유가 있으면 **1(기본 리듬 그대로)** — 짧은 문장을 괜히 서두르지 않는다.
 * 예산을 모르면(마지막 구간·구간 정보 없음) 1 이다.
 */
export function beatPace(text: string, budgetFrames?: number): number {
  if (typeof budgetFrames !== 'number' || !Number.isFinite(budgetFrames) || budgetFrames <= 0) return 1;
  const natural = titleSettleFrame(text);
  const target = budgetFrames * BEAT_READ_HOLD;
  if (natural <= target) return 1;
  return Math.max(MIN_PACE, target / natural);
}
/** 카메라 푸시인 — 씬 8초(240f)에 걸쳐 1.0→1.06(2.5D 느낌·안전 영역 안). */
const PUSH_IN_FRAMES = 240;

interface Glyph {
  ch: string;
  /** 등장 시작 프레임 */
  enterAt: number;
  /** keyword 범위 안의 글자 */
  hot: boolean;
  /** 전체 글자 순번(물결 위상) */
  idx: number;
}

/** 제목 → 어절(공백 보존) → 글자. ★공백도 글자 span 으로 살린다★ — textContent 가 원문과 동일해야 한다. */
export function layoutGlyphs(title: string, keyword?: string, pace = 1): Glyph[][] {
  const kwStart = keyword && keyword.length > 0 ? title.indexOf(keyword) : -1;
  const kwEnd = kwStart >= 0 ? kwStart + (keyword as string).length : -1;
  const p = Number.isFinite(pace) && pace > 0 ? pace : 1;
  const words: Glyph[][] = [];
  let cur: Glyph[] = [];
  let enter = 0;
  let idx = 0;
  for (let i = 0; i < title.length; i++) {
    const ch = title[i];
    if (ch === ' ') {
      cur.push({ ch, enterAt: enter, hot: false, idx });
      words.push(cur);
      cur = [];
      enter += WORD_GAP * p;
      idx++;
      continue;
    }
    cur.push({ ch, enterAt: enter, hot: i >= kwStart && i < kwEnd, idx });
    enter += CHAR_STAGGER * p;
    idx++;
  }
  if (cur.length) words.push(cur);
  return words;
}

/** 어절 앞뒤 문장부호 — 폴백 강조어는 부호를 뺀 알맹이만 강조한다(«지금입니다.» → «지금입니다»). */
const EDGE_PUNCT = /^[\s.,!?…~"'`()\[\]{}<>:;·\-]+|[\s.,!?…~"'`()\[\]{}<>:;·\-]+$/g;

/**
 * 🔤🎞 강조어 결정(회장님 2026-09-07 «폴백 강조어 넣어»).
 *  ① 디렉터·편집이 준 keyword 가 제목 안에 있으면 그대로.
 *  ② 없으면 폴백: 가장 긴 어절(문장부호 제외 · 동률이면 뒤쪽 = 문장의 결론부). dev 실측에서 디렉터가
 *     커버 fullText 에 keyword 를 주지 않아 «강조 단어 확대»가 실산출에서 0회 발동한 것이 근거.
 *  제목이 한 어절뿐이면 그 어절(전체 강조). 알맹이가 비면 undefined(강조 없음).
 */
export function resolveKineticKeyword(title: string, keyword?: string): string | undefined {
  if (keyword && keyword.length > 0 && title.includes(keyword)) return keyword;
  let best: string | undefined;
  for (const raw of title.split(' ')) {
    const w = raw.replace(EDGE_PUNCT, '');
    if (!w || !title.includes(w)) continue;
    if (!best || w.length >= best.length) best = w;
  }
  return best;
}

/** 마지막 글자가 안착하는 프레임 — 부제 진입·강조 발동의 기준점. */
export function titleSettleFrame(title: string, pace = 1): number {
  const p = Number.isFinite(pace) && pace > 0 ? pace : 1;
  const words = layoutGlyphs(title, undefined, p);
  const last = words.flat().reduce((m, g) => Math.max(m, g.enterAt), 0);
  return last + CHAR_ENTER_FRAMES * p;
}

/**
 * 🔤🎞 문장 교대 구간(W3 · 회장님 승인 2026-09-08 A안) — 이 씬의 나레이션 문장 하나와 그 문장이
 *  시작하는 ★씬 상대 프레임★.
 *
 * ★왜 나레이션인가★: 벤치마크의 3구간(0–2.5/2.5–5.5/5.5–8)은 **나레이션 없는 10초 카드** 전제다.
 *  우리 씬 길이는 나레이션이 정하므로 그 비율을 그대로 옮기면 글자가 말과 어긋난다. 씬의 문장과
 *  시각은 이미 세그먼트에 있으니(브릿지 splitBlueprintNarration) 그걸 쓰면 «말에 맞춰 문장이
 *  바뀐다»가 공짜로 따라오고, 음성 재타이밍(paceStorySegmentsByVoice)도 자동으로 반영된다.
 *
 * ★content 가 아니라 prop 인 이유★: 장표 컴포넌트는 `slide.content` 만 받는데(Overlay169 스프레드)
 *  씬 문맥은 거기 없다. content 에 실으면 fullText 와의 데이터 계약 동일성이 깨져 W2 가 세운
 *  동치 클래스(slide-auto.canonSlideType)의 근거가 흔들린다 — 그래서 렌더 경계에서만 넘긴다.
 */
export {sceneBeatsFrom, normalizeBeats} from '../../lib/templates/slide-beats';
import {normalizeBeats, type KineticBeat} from '../../lib/templates/slide-beats';
export type {KineticBeat, BeatSourceSeg} from '../../lib/templates/slide-beats';

/** 이 프레임이 속한 구간 index — 첫 구간 시작 전이면 -1(그때는 제목이 주역이다). */
export function activeBeatIndex(beats: KineticBeat[], f: number): number {
  let idx = -1;
  for (let i = 0; i < beats.length; i++) {
    if (f >= beats[i].atFrame) idx = i;
    else break;
  }
  return idx;
}

const KineticGlyph: React.FC<{ g: Glyph; f: number; fps: number; colors: AtmosphereColors; settleAt: number; px: (v: number) => number; pace: number; stage?: boolean }> = ({ g, f, fps, colors, settleAt, px, pace, stage = false }) => {
  // 등장: 스프링 바운스(아래→제자리·0.6→1 확대·투명→불투명). 양방향 clamp 는 spring 자체가 보장.
  //   ★등장 시간도 `pace` 로 함께 줄인다★ — 간격만 줄이면 «빨리 시작하는데 하나하나는 느린» 리듬이 된다.
  const enterFrames = CHAR_ENTER_FRAMES * pace;
  const p = spring({ frame: Math.max(0, f - g.enterAt), fps, config: SPRING.slam, durationInFrames: enterFrames + 6 * pace });
  // 🧊 무대 안에서는 24px 에서 올라온다 — 70px 이면 마지막 줄 글자가 등장하며 도형 아래로 15px 나갔다(09-30 전 프레임 검사 · 주제2 13초). 무대 밖 종전.
  const drop = (1 - p) * px(stage ? 24 : 70);
  const scaleIn = 0.6 + 0.4 * p;
  // 안착 후 잔물결(~~): 글자 순번 위상차 — 강조어는 진폭 2.2배.
  const settled = f >= g.enterAt + enterFrames;
  const wave = settled ? Math.sin((f - g.enterAt) * 0.16 + g.idx * 0.55) * px(g.hot ? 9 : 4) : 0;
  // 강조어: 제목 전체가 안착한 뒤 한 번 더 튀어 오르며 1.18배(벤치마크 «강조 단어 확대»).
  const hotP = g.hot ? spring({ frame: Math.max(0, f - settleAt), fps, config: SPRING.bounce, durationInFrames: 20 }) : 0;
  const scale = scaleIn * (1 + 0.18 * hotP);
  const opacity = interpolate(p, [0, 0.25, 1], [0, 1, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  return (
    <span
      style={{
        display: 'inline-block',
        whiteSpace: 'pre',
        opacity,
        transform: `translateY(${drop + wave}px) scale(${scale})`,
        transformOrigin: '50% 80%',
        color: g.hot && hotP > 0.01 ? colors.accent : undefined,
        textShadow: g.hot && hotP > 0.01 ? `0 0 ${px(24)}px ${colors.accentGlow}` : undefined,
      }}
    >
      {g.ch}
    </span>
  );
};

/**
 * 모션 타이포 장표. `f` = 씬 상대 프레임(Overlay169 계약). 텍스트는 글자 단위 span 으로 쪼개되
 * 원문 순서·공백을 그대로 보존한다(`data-kinetic-title` 의 textContent === title).
 */
export const KineticTypo: React.FC<LayoutBaseProps & { title: string; sub?: string; beats?: unknown }> = (p) => {
  const { colors: C, f, revealMode = 'all', keyword } = p;
  const { fps } = useVideoConfig();
  const s = useSafeArea(p.aspect);
  const title = typeof p.title === 'string' ? p.title : String(p.title ?? '');
  // 🔤🎞 문장 교대 — 구간이 ★2개 이상★일 때만 발동한다. 1개면 교대할 것이 없어 종전과 같고(자연 열화),
  //   구간이 전혀 없는 씬(샘플·손편집 작업물)도 종전 제목 카드 그대로다 = 회귀 0.
  const beats = normalizeBeats(p.beats);
  const alternating = beats.length >= 2;
  const beatIdx = alternating ? activeBeatIndex(beats, f) : -1;
  /** 지금 크게 그릴 문장 — 교대 중이면 그 구간 문장, 아니면 제목. */
  const hero = beatIdx >= 0 ? beats[beatIdx].text : title;
  /** 글자 애니는 ★구간마다 다시★ 재생한다 — 씬 프레임을 그대로 쓰면 두 번째 문장이 이미 안착한 채 뜬다. */
  const heroF = beatIdx >= 0 ? Math.max(0, f - beats[beatIdx].atFrame) : f;
  /** 🕐 등장 리듬 자동 조절 — 이 구간 예산 안에 문장이 다 뜨도록 배율을 정한다(여유 있으면 1).
   *  예산이 없는 자리(제목 카드·마지막 구간의 씬 끝 미상)는 종전 리듬 그대로다 = 회귀 0. */
  const beatBudget = beatIdx >= 0 ? beats[beatIdx].untilFrame : undefined;
  const pace = beatBudget !== undefined ? beatPace(hero, beatBudget - beats[beatIdx].atFrame) : 1;
  // 🧊 무대 안은 마지막 두 어절을 붙여 고아를 막는다(briefGlueLastWords · 강조어는 원문에서 고르고 붙인 글에 맞춰 옮긴다).
  const onStage = React.useContext(SlideStageInsetContext) !== null;
  const heroShown = onStage ? briefGlueLastWords(hero, s.px(T.hero), s.W - s.safeL - s.safeR) : hero;
  const kw = resolveKineticKeyword(hero, keyword);
  const kwShown = kw && !heroShown.includes(kw) ? kw.replace(/ /g, '\u00A0') : kw;
  const words = layoutGlyphs(heroShown, kwShown, pace);
  const settleAt = titleSettleFrame(hero, pace);
  // 🧰 «모션 부품»(2026-09-30 · motion_card_kit) — 부제만 부품 상자 규율(진입 토큰 + 어절 등장). 글자 애니(제목)는 이 파일 고유라 그대로.
  const kit = p.motionKit === true;
  const subAt = settleAt + enterAtFor(1, revealMode, 4);
  const subAnimRaw = slideIn(heroF, { enterAt: subAt, duration: 20, kit, fps });
  const subAnim = kit ? { ...subAnimRaw, opacity: 1 } : subAnimRaw; // kit: 어절이 켜짐을 맡는다(완성 프레임은 둘 다 opacity 1)
  // 2.5D 카메라 — 씬 전체가 천천히 다가온다(글자·파티클이 함께 확대돼 깊이감).
  const push = interpolate(f, [0, PUSH_IN_FRAMES], [1, 1.06], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  // 🧊 «모션 브리핑» 무대 안이면 배경·입자·색종이를 그리지 않는다 — 무대가 캔버스와 도형을 이미 그렸다(화면 전체를 덮으면 도형이 사라진다).
  //   무대 밖(컨텍스트 null)은 종전 그대로.
  const gradId = `kt-bg-${C.panel.replace(/[^a-zA-Z0-9]/g, '')}-${C.bg.replace(/[^a-zA-Z0-9]/g, '')}`;
  const glowId = `kt-glow-${C.accent.replace(/[^a-zA-Z0-9]/g, '')}`;
  return (
    <AbsoluteFill style={{ overflow: 'hidden' }}>
      {/* 배경 — SVG 그라디언트(렌더 세이프) + 상단 악센트 글로우. 가족 팔레트 그대로(밝은 Gov=밝은 그라디언트). */}
      {onStage ? null : <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0 }} preserveAspectRatio="none">
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={C.panel} />
            <stop offset="100%" stopColor={C.bg} />
          </linearGradient>
          <radialGradient id={glowId} cx="50%" cy="20%" r="70%">
            <stop offset="0%" stopColor={C.accent} stopOpacity={C.isDark === false ? 0.22 : 0.3} />
            <stop offset="100%" stopColor={C.accent} stopOpacity={0} />
          </radialGradient>
        </defs>
        <rect x="0" y="0" width="100%" height="100%" fill={`url(#${gradId})`} />
        <rect x="0" y="0" width="100%" height="100%" fill={`url(#${glowId})`} />
      </svg>}
      <div style={{ position: 'absolute', inset: 0, transform: `scale(${push})`, transformOrigin: '50% 45%' }}>
        {/* 파티클 — 부유 오브(컨페티 느낌) + 상승 입자. 기존 렌더 증명 컴포넌트 재사용(파티클 수 상한 내장). */}
        {onStage ? null : <FloatingOrbs f={f} colors={C} scheme="accent" />}
        {onStage ? null : <ParticleRise f={f} colors={C} scheme="accent" />}
        {/* 🎊 색종이 — 벤치마크의 «컨페티». 원형 입자 두 겹 위에 회전하는 직사각형을 얹어 결을 만든다. */}
        {onStage ? null : <Confetti f={f} colors={C} scheme="accent" />}
        <AbsoluteFill
          style={{
            paddingTop: s.safeT, paddingBottom: s.safeB, paddingLeft: s.safeL, paddingRight: s.safeR,
            display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center',
          }}
        >
          {/* 🔤🎞 교대 중에는 제목이 ★작은 머리말★로 물러난다 — 큰 자리는 지금 말하는 문장 몫이다.
              (교대가 아니면 제목이 그대로 주역이라 이 줄은 없다 = 종전 화면 그대로.) */}
          {alternating && title ? (
            <div
              data-kinetic-eyebrow=""
              style={{ marginBottom: s.px(20), fontSize: s.px(T.body), fontWeight: FW.medium, lineHeight: LINE_HEIGHT.normal, color: C.textSub, opacity: 0.85, ...KO_TEXT }}
            >
              {title}
            </div>
          ) : null}
          <div
            key={beatIdx}
            data-kinetic-title=""
            style={{
              // 🧊 무대 안은 일반 글줄 흐름 + 줄 고르게(10-01 회장님 «단어 잘리는 것만 없게» · «…확인할 세 / 가지»):
              //   어절 조각 flex 에는 `text-wrap: balance` 가 먹지 않는다 → 인라인 블록 어절을 가운데 정렬 글줄로 흘린다(줄 수는 브라우저가 · 길이는 고르게).
              //   브라우저 내보내기도 실제 배치를 읽어 같은 줄바꿈이다. 무대 밖은 종전 flex.
              ...(onStage
                ? { display: 'block', textAlign: 'center' as const, textWrap: 'balance' as const }
                : { display: 'flex', flexWrap: 'wrap' as const, justifyContent: 'center', alignItems: 'baseline' }),
              fontSize: s.px(T.hero), fontWeight: FW.extrabold, lineHeight: LINE_HEIGHT.tight, color: C.textMain,
              maxWidth: '100%', ...KO_TEXT, whiteSpace: 'normal',
            }}
          >
            {words.map((w, wi) => (
              <span key={wi} style={{ display: 'inline-block', whiteSpace: 'pre' }}>
                {w.map((g, gi) => (
                  <KineticGlyph key={gi} g={g} f={heroF} fps={fps} colors={C} settleAt={settleAt} px={s.px} pace={pace} stage={onStage} />
                ))}
              </span>
            ))}
          </div>
          {/* 부제는 제목이 주역일 때만 — 교대 중엔 머리말·문장과 3층이 되어 화면이 시끄럽다. */}
          {!alternating && p.sub ? (
            <div
              data-kinetic-sub=""
              style={{ ...subAnim, marginTop: s.px(32), fontSize: s.px(T.body), fontWeight: FW.medium, lineHeight: LINE_HEIGHT.normal, color: C.textSub, ...KO_TEXT }}
            >
              <KitWords kit={kit} fps={fps} f={heroF} at={subAt} text={p.sub}>{p.sub}</KitWords>
            </div>
          ) : null}
        </AbsoluteFill>
      </div>
    </AbsoluteFill>
  );
};
